import { Seo } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { Reveal } from "../lib/motion";
import { CTASection } from "../components/site/CTASection";
import { SERVICES, HOW_WE_WORK, FAQS } from "../data/services";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Check } from "lucide-react";

export default function Services() {
  return (
    <>
      <Seo
        title="Services | Arc & Hollow — Residential Interior Design, London"
        description="Full-service residential interiors, interior architecture and renovation, and styling, art and FF&E for considered London homes."
        path="/services"
      />
      <PageHero
        overline="Services"
        titleLines={["From first idea", "to final styling."]}
        intro="Three connected services, commissioned individually or brought together as one seamless design journey—from the earliest spatial decisions to the final placement of furniture, art and objects."
      />

      {SERVICES.map((s, idx) => (
        <section
          key={s.slug}
          id={s.slug}
          className={`${idx % 2 === 1 ? "bg-limestone/40" : ""} py-20 md:py-28`}
          data-testid={`service-${s.slug}`}
        >
          <div className="container-x grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <span className="font-serif text-6xl font-light text-bronze/60">{s.index}</span>
                <h2 className="mt-6 font-serif text-3xl md:text-4xl font-light leading-tight">{s.title}</h2>
                <p className="mt-6 text-lg font-light leading-relaxed text-olive">{s.summary}</p>
                <p className="mt-4 font-light leading-relaxed text-olive/90">{s.detail}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <Reveal delay={0.1}>
                <p className="overline text-bronze mb-6">What it includes</p>
                <ul className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                  {s.includes.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-light border-b border-charcoal/10 pb-4">
                      <Check size={15} strokeWidth={1.6} className="mt-1 text-bronze shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <section className="bg-charcoal text-chalk py-24 md:py-32" data-testid="how-we-work">
        <div className="container-x">
          <Reveal><p className="overline text-bronze">How we work</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light max-w-2xl">A clear, unhurried process</h2>
          </Reveal>
          <div className="mt-16 grid gap-y-12 gap-x-10 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_WE_WORK.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.1} className="border-t border-chalk/20 pt-6">
                <span className="font-serif text-5xl font-light text-bronze">{step.n}</span>
                <h3 className="mt-6 text-xl font-medium tracking-wide">{step.title}</h3>
                <p className="mt-3 text-sm text-chalk/60 font-light leading-relaxed">{step.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-24 md:py-32" data-testid="faq">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal><p className="overline text-bronze">Common questions</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-serif text-3xl md:text-4xl font-light">Working with the studio</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-charcoal/15" data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-serif text-xl md:text-2xl font-light py-6 hover:no-underline hover:text-bronze">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base font-light leading-relaxed text-olive pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
